// «La carta de CARO» (docs/plan-cartas.md): the monthly letter, sent by email
// AND published as a page at /newsletter/<AAAA>/<MM>/<slug> in ES/EN/FR (the
// `newsletter` segment is never translated, only the slug). Pure (no DB, no
// DOM): shared by the Worker (admin API, public pages), the SPA and tests. The
// Zod schema of a draft lives in worker/lib/adminLetters.ts; the CLI mirrors
// the limits below in scripts/lib/admin-cli-core.mjs (parity test).

import type { Locale } from '@/types';
import { madridOffset } from '@/lib/madridTime';
import { INTL_LOCALE } from '@/lib/seo';

/** letter = a letter of its own (indexable page); edit = it presents an Edit post (page noindex, out of the sitemap). */
export const LETTER_KINDS = ['letter', 'edit'] as const;
export type LetterKind = (typeof LETTER_KINDS)[number];

export const MAX_LETTER_SLUG = 80;
export const MAX_LETTER_SUBJECT = 70;
export const MAX_LETTER_PREHEADER = 110;
export const MAX_LETTER_TITLE = 120;
export const MAX_LETTER_SUMMARY = 160;
export const MAX_LETTER_BODY = 20_000;

/** One language of a letter. `slug` is that language's URL slug; `body` the Edit Markdown (lib/markdown). */
export interface LetterLocaleContent {
  slug: string;
  subject: string;
  preheader: string;
  title: string;
  summary: string;
  body: string;
}

export type LetterContent = Record<Locale, LetterLocaleContent>;

export interface Letter {
  id: string;
  /** «AAAA-MM»: one letter per month (UNIQUE in D1). */
  month: string;
  kind: LetterKind;
  /** Spanish slug of the Edit article (kind = edit only). */
  articleSlug: string | null;
  /** Optional `/media/…` image: og:image of the page and header of the email. */
  image: string | null;
  content: LetterContent;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  testSentAt: string | null;
  publishedAt: string | null;
  sentCount: number;
  lastSentAt: string | null;
}

export const LETTER_MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** The calendar month («AAAA-MM») in Madrid at `now`. */
export function madridMonth(now: number): string {
  return new Date(now + madridOffset(now)).toISOString().slice(0, 7);
}

/** «2026-11» → { year: '2026', month: '11' }, or null when malformed. */
export function monthParts(month: string): { year: string; month: string } | null {
  const m = LETTER_MONTH_RE.exec(month);
  return m?.[1] && m[2] ? { year: m[1], month: m[2] } : null;
}

/** Anything with a month and a slug per language (a Letter, a PublicLetter, an archive entry). */
type LetterSlugs = { month: string; content: Record<Locale, { slug: string }> };

/** The LOGICAL path of the letter in `locale` (no locale prefix: localizedPath / lp add it). */
export function letterPath(letter: LetterSlugs, locale: Locale): string {
  const [year, month] = letter.month.split('-');
  return `/newsletter/${year}/${month}/${letter.content[locale].slug}`;
}

/** The letter's logical path in every language (hreflang alternates, sitemap). */
export function letterLocalePaths(letter: LetterSlugs): Record<Locale, string> {
  return { es: letterPath(letter, 'es'), en: letterPath(letter, 'en'), fr: letterPath(letter, 'fr') };
}

/** «noviembre de 2026» / «November 2026» / «novembre 2026». */
export function letterMonthLabel(month: string, locale: Locale): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, 1)).toLocaleDateString(INTL_LOCALE[locale], {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** One language of a public letter: what its page and the archive show (no subject / preheader). */
export interface PublicLetterText {
  slug: string;
  title: string;
  summary: string;
}

/** A published letter as the public API serves it (GET /api/letters/:year/:month/:slug). */
export interface PublicLetter {
  month: string;
  kind: LetterKind;
  image: string | null;
  publishedAt: string;
  content: Record<Locale, PublicLetterText & { body: string }>;
  /** `edit` letters: the Edit post, LOGICAL path + its title and excerpt per language. */
  article: { path: string; image: string | null; text: Record<Locale, { title: string; excerpt: string }> } | null;
}

/** An entry of the /newsletter archive (GET /api/letters). */
export type PublicLetterSummary = Pick<PublicLetter, 'month' | 'kind' | 'publishedAt'> & {
  content: Record<Locale, PublicLetterText>;
};

/**
 * One letter in /admin → Cartas (GET /api/admin/letters, fase 4). Counts only,
 * never addresses. `preview` = the email exactly as a subscriber of each
 * language gets it (sample unsubscribe link); `page` = its web page.
 */
export interface AdminLetterRow {
  letter: Letter;
  /** Why «Publicar y enviar» may not run now (409 code), or null. */
  blocked: LetterSendBlock | null;
  /** The last test was sent after the last change of the draft. */
  testCurrent: boolean;
  counts: { sent: number; waiting: number; failed: number; skipped: number; sending: number };
  /** La carta subscribers who have not received it yet, per language (the N of the button). */
  eligible: Record<Locale, number>;
  page: PublicLetter;
  preview: Record<Locale, { subject: string; preheader: string; html: string; text: string }>;
}

/** Result of «Enviarme una prueba»: the three languages to the signed-in admin. */
export interface AdminLetterTestResult {
  to: string;
  sent: number;
  failed: number;
  skipped: number;
  /** test_sent_at recorded (false: the draft changed meanwhile, or nothing went out). */
  recorded: boolean;
  /** The optional extra copy (one language, real subject), e.g. to mail-tester. */
  also?: { to: string; locale: Locale; status: 'sent' | 'failed' | 'skipped' };
}

/** Result of one «Publicar y enviar» / «Seguir enviando» press (at most 40 emails). */
export interface AdminLetterSendResult {
  sent: number;
  failed: number;
  skipped: number;
  remaining: number;
  /** Logical ES path of the public page (published now). */
  path: string;
}

/** <title> and meta description of a letter page (same on the Worker and the client). */
export function letterPageText(
  letter: { content: Record<Locale, PublicLetterText> },
  locale: Locale,
): { title: string; description: string } {
  return { title: letter.content[locale].title, description: letter.content[locale].summary };
}

/**
 * The language whose slug is `slug`, or null. `preferred` (the URL's language)
 * wins when several languages share the slug, so a shared slug never looks
 * like another language's URL; then ES, EN, FR.
 */
export function letterLocaleForSlug(letter: Pick<LetterSlugs, 'content'>, slug: string, preferred?: Locale): Locale | null {
  if (preferred && letter.content[preferred].slug === slug) return preferred;
  for (const locale of ['es', 'en', 'fr'] as const) {
    if (letter.content[locale].slug === slug) return locale;
  }
  return null;
}

/** A letter is public once «Publicar y enviar» set `published_at` (no scheduling). */
export function isLetterPublic(letter: Pick<Letter, 'publishedAt'>): boolean {
  return letter.publishedAt !== null;
}

/** The page of an `edit` letter is noindex: its content is the article's. */
export function isLetterIndexable(letter: Pick<Letter, 'kind' | 'publishedAt'>): boolean {
  return isLetterPublic(letter) && letter.kind === 'letter';
}

/**
 * Why «Publicar y enviar» may not go now (409 code), or null when it may.
 * - Not published yet: only in its own Madrid month, only after a test of THIS
 *   version (`test_sent_at` ≥ `updated_at`) and, for an `edit` letter, only
 *   when the article is public.
 * - Published: later presses finish the queue (batches of 40) only within the
 *   same Madrid month, so nobody gets two letters in one calendar month (and
 *   the 12-month purge of letter_sends never lets a press email someone twice).
 */
export type LetterSendBlock = 'wrong_month' | 'test_required' | 'article_not_published';
export function letterSendBlock(
  letter: Pick<Letter, 'month' | 'kind' | 'updatedAt' | 'testSentAt' | 'publishedAt'>,
  now: number,
  articlePublished: boolean,
): LetterSendBlock | null {
  if (letter.month !== madridMonth(now)) return 'wrong_month';
  if (letter.publishedAt !== null) return null;
  if (!letter.testSentAt || letter.testSentAt < letter.updatedAt) return 'test_required';
  if (letter.kind === 'edit' && !articlePublished) return 'article_not_published';
  return null;
}
